import { livePresentationTarget } from "./presentation";
import { isPlayableMapScene } from "./media-map";
import type { CueSequence, CueStep, Scene, WorkspaceState } from "../types";

export function sortedCueSteps(sequence: CueSequence): CueStep[] {
  return [...sequence.steps].sort((a, b) => Math.max(0, a.atMs) - Math.max(0, b.atMs) || a.id.localeCompare(b.id));
}

export function cueDuration(sequence: CueSequence): number {
  return sortedCueSteps(sequence).at(-1)?.atMs ?? 0;
}

export function cueReferencedSceneIds(sequence: CueSequence): string[] {
  return [...new Set(sequence.steps.filter((step) => step.action === "scene" && step.sceneId).map((step) => step.sceneId!))];
}

function cueTargetScene(state: WorkspaceState, step: CueStep): Scene | undefined {
  const targetId = step.sceneId ?? livePresentationTarget(state.presentation);
  return state.scenes.find((scene) => scene.id === targetId);
}

export function applyCueStep(state: WorkspaceState, step: CueStep, now = Date.now()): WorkspaceState {
  if (step.action === "audio") return { ...state, presentation: { ...state.presentation, cuePlayback: state.presentation.cuePlayback ? { ...state.presentation.cuePlayback, stepIndex: state.presentation.cuePlayback.stepIndex + 1 } : undefined } };
  if (step.action === "blackout") return { ...state, presentation: { ...state.presentation, blackout: step.enabled ?? true, cuePlayback: state.presentation.cuePlayback ? { ...state.presentation.cuePlayback, stepIndex: state.presentation.cuePlayback.stepIndex + 1 } : undefined } };
  if (step.action === "cover") return { ...state, presentation: { ...state.presentation, covered: step.enabled ?? true, cuePlayback: state.presentation.cuePlayback ? { ...state.presentation.cuePlayback, stepIndex: state.presentation.cuePlayback.stepIndex + 1 } : undefined } };
  if (step.action === "scene") {
    const target = cueTargetScene(state, step); if (!target) return state;
    const presentation = isPlayableMapScene(target)
      ? { ...state.presentation, activeSceneId: target.id, overrideSceneId: undefined, stagedSceneId: undefined, stagedReady: false, stagingError: undefined, tokenPlacementMode: false, showFitScreen: false, blackout: false, sessionWaiting: false }
      : { ...state.presentation, overrideSceneId: target.id, stagedSceneId: undefined, stagedReady: false, stagingError: undefined, tokenPlacementMode: false, blackout: false, sessionWaiting: false };
    return { ...state, presentation: { ...presentation, cuePlayback: presentation.cuePlayback ? { ...presentation.cuePlayback, stepIndex: presentation.cuePlayback.stepIndex + 1 } : undefined } };
  }
  const target = cueTargetScene(state, step); if (!isPlayableMapScene(target)) return state;
  const nextScene: Scene = step.action === "time-of-day"
    ? { ...target, timeOfDay: step.timeOfDay ?? target.timeOfDay, updatedAt: now }
    : { ...target, rain: step.rain ?? target.rain, snow: step.snow ?? target.snow, atmosphericFog: step.atmosphericFog ?? target.atmosphericFog, lightning: step.lightning ?? target.lightning, windSpeed: step.windSpeed ?? target.windSpeed, updatedAt: now };
  return {
    ...state,
    scenes: state.scenes.map((scene) => scene.id === nextScene.id ? nextScene : scene),
    presentation: { ...state.presentation, cuePlayback: state.presentation.cuePlayback ? { ...state.presentation.cuePlayback, stepIndex: state.presentation.cuePlayback.stepIndex + 1 } : undefined },
  };
}

export function startCuePlayback(state: WorkspaceState, sequence: CueSequence, now = Date.now()): WorkspaceState {
  return { ...state, presentation: { ...state.presentation, cuePlayback: { sequenceId: sequence.id, name: sequence.name, startedAt: now, stepIndex: 0 } } };
}

export function finishCuePlayback(state: WorkspaceState): WorkspaceState {
  return { ...state, presentation: { ...state.presentation, cuePlayback: undefined } };
}

export function validateCueSequence(sequence: CueSequence, state: WorkspaceState): string[] {
  const errors: string[] = [];
  if (!sequence.name.trim()) errors.push("Give the cue sequence a name.");
  if (!sequence.steps.length) errors.push("Add at least one cue.");
  for (const step of sequence.steps) {
    if (!Number.isFinite(step.atMs) || step.atMs < 0) errors.push("Cue times must be zero or greater.");
    if (step.action === "scene" && (!step.sceneId || !state.scenes.some((scene) => scene.id === step.sceneId && scene.campaignId === sequence.campaignId))) errors.push("A scene cue points to an unavailable scene.");
    if (step.action === "audio" && (!step.audioAssetId || !state.assets.some((asset) => asset.id === step.audioAssetId && asset.kind === "audio"))) errors.push("An audio cue points to an unavailable track.");
  }
  return [...new Set(errors)];
}
